// TODO(api): Real backend integration. Currently scaffolded only.
// Maps the server error envelope { error: { code, message, fields, meta } }
// to QuestionnaireDesignApiError. Activated by setting VITE_USE_MOCK_API=false;
// see questionnaire-design.adapter.ts.

import { proTreeHttpApi } from '@/features/pro-tree/api/pro-tree.api';
import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';

import {
  QuestionnaireDesignApiError,
  type CopyQuestionInput,
  type CreateQuestionInput,
  type MultiSelectConfig,
  type Question,
  type QuestionnaireDesignApi,
  type QuestionnaireDesignApiErrorCode,
  type ScoringAdvanced,
  type UpdateQuestionInput,
} from '../types/questionnaire-design.types';

interface ApiEnvelope<T> {
  data: T;
}

// The BE lumps `multiConfig` INTO the `advanced` blob, while the FE model keeps
// it as a sibling field (SHCT-240). Adapt at the wire boundary in both
// directions so neither side has to change its internal shape.
type WireAdvanced = ScoringAdvanced & { multiConfig?: MultiSelectConfig };
type WireQuestion = Omit<Question, 'advanced'> & { advanced: WireAdvanced };

// Outbound: fold a sibling `multiConfig` into `advanced` before sending.
function toWireBody(
  input: CreateQuestionInput | UpdateQuestionInput,
): Record<string, unknown> {
  const { multiConfig, advanced, ...rest } = input as (CreateQuestionInput &
    UpdateQuestionInput) & { multiConfig?: MultiSelectConfig };
  const wireAdvanced =
    advanced || multiConfig
      ? { ...(advanced ?? {}), ...(multiConfig ? { multiConfig } : {}) }
      : undefined;
  return { ...rest, ...(wireAdvanced ? { advanced: wireAdvanced } : {}) };
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  const payload = candidate.response?.data?.error;
  throw new QuestionnaireDesignApiError(
    (payload?.code as QuestionnaireDesignApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields, meta: payload?.meta },
  );
}

// The BE serializes with `@JsonInclude(NON_NULL)`, so a top-level question
// omits `parentQid` entirely rather than sending `null`. The FE contract is
// `string | null` and the layout splitter keys on `parentQid === null`
// (strict), so a missing key would arrive as `undefined` and silently drop
// every top-level question. Normalize at the wire boundary. Also lift the
// BE-nested `advanced.multiConfig` back out to the sibling field the FE reads.
function normalizeQuestion(raw: WireQuestion): Question {
  const { multiConfig: nested, ...scoring } = (raw.advanced ?? {}) as WireAdvanced;
  return {
    ...raw,
    parentQid: raw.parentQid ?? null,
    advanced: scoring as ScoringAdvanced,
    multiConfig: nested ?? raw.multiConfig,
  };
}

export const questionnaireDesignHttpApi: QuestionnaireDesignApi = {
  async listProTree() {
    return proTreeHttpApi.listProTree();
  },

  async listAllQuestions(): Promise<Question[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<WireQuestion[]>>(
        `${endpoints.questionnaireDesignQuestions}`,
      );
      return res.data.data.map(normalizeQuestion);
    } catch (e) {
      mapError(e);
    }
  },

  async listQuestions(itemId: string): Promise<Question[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<WireQuestion[]>>(
        `${endpoints.questionnaireDesignItems}/${itemId}/questions`,
      );
      return res.data.data.map(normalizeQuestion);
    } catch (e) {
      mapError(e);
    }
  },

  async createQuestion(input: CreateQuestionInput): Promise<Question> {
    try {
      const res = await httpClient.post<ApiEnvelope<WireQuestion>>(
        `${endpoints.questionnaireDesignItems}/${input.itemId}/questions`,
        toWireBody(input),
      );
      return normalizeQuestion(res.data.data);
    } catch (e) {
      mapError(e);
    }
  },

  async updateQuestion(qid: string, input: UpdateQuestionInput): Promise<Question> {
    try {
      const res = await httpClient.patch<ApiEnvelope<WireQuestion>>(
        `${endpoints.questionnaireDesignQuestions}/${qid}`,
        toWireBody(input),
      );
      return normalizeQuestion(res.data.data);
    } catch (e) {
      mapError(e);
    }
  },

  async deleteQuestion(qid: string): Promise<void> {
    try {
      await httpClient.delete(`${endpoints.questionnaireDesignQuestions}/${qid}`);
    } catch (e) {
      mapError(e);
    }
  },

  async copyQuestion(qid: string, input: CopyQuestionInput): Promise<Question> {
    try {
      const res = await httpClient.post<ApiEnvelope<WireQuestion>>(
        `${endpoints.questionnaireDesignQuestions}/${qid}/copy`,
        input,
      );
      return normalizeQuestion(res.data.data);
    } catch (e) {
      mapError(e);
    }
  },
};
